import React from 'react';
import { Building2, CalendarX, CheckSquare, Mail, UserPlus } from 'lucide-react';
import StatCard from './StatCard';
import { dashboardStatAccents, type DashboardAccent } from '../themeHelpers';
import { useDashboardStats } from '../useData';
import { useAuth } from '../../../contexts/AuthContext';
import { useClients } from '../../clients/useData';
import { useTodos } from '../../crm-todos/useData';
import { useAppointments } from '../../crm-appointments/useData';
import { useEmails } from '../../emails/useData';

export type MetricKey =
  | 'totalClients'
  | 'pendingTodos'
  | 'newClientsThisMonth'
  | 'missedAppointments'
  | 'unreadEmailsCount';

const icons: Record<MetricKey, React.ReactNode> = {
  totalClients: <Building2 aria-hidden />,
  pendingTodos: <CheckSquare aria-hidden />,
  newClientsThisMonth: <UserPlus aria-hidden />,
  missedAppointments: <CalendarX aria-hidden />,
  unreadEmailsCount: <Mail aria-hidden />,
};

const accents: Record<MetricKey, DashboardAccent> = {
  totalClients: dashboardStatAccents.clients,
  pendingTodos: dashboardStatAccents.todos,
  newClientsThisMonth: dashboardStatAccents.growth,
  missedAppointments: dashboardStatAccents.appointments,
  unreadEmailsCount: dashboardStatAccents.email,
};

const defaultTitles: Record<MetricKey, string> = {
  totalClients: 'Total Clients',
  pendingTodos: 'Pending Todos',
  newClientsThisMonth: 'New Clients (Month)',
  missedAppointments: 'Missed Appointments',
  unreadEmailsCount: 'Unread Emails',
};

export interface StatCardWidgetProps {
  metricKey?: MetricKey;
  title?: string;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  scope?: 'myWork' | 'team';
}

const StatCardWidget: React.FC<StatCardWidgetProps> = ({
  metricKey: metricKeyProp,
  title,
  settings,
  scope,
  embedded,
}) => {
  const { can } = useAuth();
  const canViewClients = can('clients', 'view');
  const metricKey = (settings?.metricKey ?? metricKeyProp ?? 'pendingTodos') as MetricKey;
  const needsClients =
    canViewClients &&
    (metricKey === 'totalClients' || metricKey === 'newClientsThisMonth');
  const { stats, isLoading: statsLoading } = useDashboardStats(scope);
  const { total: clientsTotal, data: clients = [] } = useClients(undefined, needsClients);
  const { todos = [] } = useTodos();
  const { appointments = [] } = useAppointments();
  const { data: emailsList = [] } = useEmails(undefined, { skip: 0, take: 500 });

  const todosArray = Array.isArray(todos) ? todos : [];
  const appointmentsArray = Array.isArray(appointments) ? appointments : [];
  const unreadEmailsCount = (Array.isArray(emailsList) ? emailsList : []).filter(
    (e: { isRead?: boolean }) => !e.isRead
  ).length;
  const pendingTodosCount = todosArray.filter((t: { todoCompleted?: boolean }) => !t.todoCompleted).length;
  const today = new Date().toISOString().slice(0, 10);
  const thisMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const newClientsThisMonthCount = clients.filter((c: { clientCreatedAt?: string }) => {
    const created = c.clientCreatedAt?.slice(0, 10);
    return created && created >= thisMonthStart;
  }).length;
  const missedAppointmentsCount = appointmentsArray.filter((a: { clientAppScheduleDateTime?: string; clientAppStatusDescr?: { as_descr?: string } }) => {
    const dt = a.clientAppScheduleDateTime?.slice(0, 10);
    const status = a.clientAppStatusDescr?.as_descr?.toLowerCase() ?? '';
    const cancelled = status.includes('cancel') || status.includes('no show');
    return dt && dt < today && !cancelled;
  }).length;

  const customTitle = typeof title === 'string' && title.trim() ? title.trim() : null;
  const displayTitle = customTitle ?? defaultTitles[metricKey];
  const scopeSubtitle =
    metricKey === 'pendingTodos'
      ? scope === 'team'
        ? 'Team'
        : 'My work'
      : undefined;

  const values: Record<MetricKey, string | number> = {
    totalClients: stats.totalClients ?? clientsTotal ?? 0,
    pendingTodos: stats.pendingTodos ?? pendingTodosCount,
    newClientsThisMonth: newClientsThisMonthCount,
    missedAppointments: missedAppointmentsCount,
    unreadEmailsCount,
  };

  return (
    <StatCard
      title={displayTitle}
      subtitle={scopeSubtitle}
      value={values[metricKey]}
      icon={icons[metricKey]}
      accent={accents[metricKey]}
      isLoading={statsLoading}
      embedded={embedded}
    />
  );
};

export default StatCardWidget;
